import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, GiftIcon, XIcon } from 'lucide-react';
import { useExitIntent } from '../hooks/useExitIntent';
import { prefetchPage } from '../utils/prefetch';
const SPECIAL_OFFER_URL = 'https://trening-funkcjonalny-365.impultienda.ar/oferta-especial?store_id=b457d47d-1679-433f-bcc6-0f9f845bf715';
export function ExitIntentModal() {
  const {
    closeOffer,
    isOpen
  } = useExitIntent();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    prefetchPage(SPECIAL_OFFER_URL);
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeOffer();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusableElements = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'));
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (!firstElement || !lastElement) return;
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeOffer, isOpen]);
  return <AnimatePresence data-id="element-160" data-mp="152zzgews2qeq">
      {isOpen && <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} role="presentation" onClick={e => {
      if (e.target === e.currentTarget) closeOffer();
    }} data-id="element-161" data-mp="151ypkdri09yl">
          <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="exit-offer-title" aria-describedby="exit-offer-description" className="relative w-full max-w-md overflow-hidden rounded-3xl border border-sky-400/30 bg-slate-900 p-6 text-center shadow-2xl shadow-blue-950/60 md:p-8" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 30
      }} data-id="element-162" data-mp="1j5y8lqwjx47v">
            <button ref={closeButtonRef} type="button" onClick={closeOffer} className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-slate-400 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400" aria-label="Zamknij ofertę specjalną" data-id="element-163" data-mp="bqjrmc73avlx">
              <XIcon size={18} aria-hidden="true" data-id="element-164" data-mp="3vbhbvn6hkgj6" />
            </button>

            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white" data-id="element-165" data-mp="3rc8kgoyx4uqs">
              <GiftIcon size={26} aria-hidden="true" data-id="element-166" data-mp="3mdymk6lt7g1h" />
            </div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-sky-400" data-id="element-167" data-mp="3hldsr52wl2xy">
              Oferta specjalna · tylko teraz
            </p>
            <h2 id="exit-offer-title" className="mb-4 font-display text-3xl font-black italic uppercase leading-[0.95] tracking-tight text-white md:text-4xl" data-id="element-168" data-mp="3ck80t9h2hjph">
              Zaczekaj! Mamy dla Ciebie prezent
            </h2>
            <p id="exit-offer-description" className="mx-auto mb-7 max-w-sm text-sm leading-relaxed text-slate-300 md:text-base" data-id="element-169" data-mp="2yo48xmto1ng6">
              Zanim wyjdziesz, sprawdź tę ekskluzywną ofertę ze zniżką, która
              jest dostępna tylko teraz. Płacisz w złotówkach.
            </p>

            <div className="flex flex-col gap-2.5" data-id="element-170" data-mp="srr3b2txfbhd">
              <a href={SPECIAL_OFFER_URL} rel="noopener" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-4 text-base font-bold uppercase tracking-wide text-white shadow-[0_10px_30px_-10px_rgba(37,99,235,0.8)] transition-[transform,background-color] duration-200 ease-out hover:-translate-y-0.5 hover:bg-blue-500 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900" data-id="element-171" data-mp="xrwn5wtyftow">
                Zobacz ofertę <ArrowRightIcon size={18} aria-hidden="true" data-id="element-172" data-mp="298d98900l8kq" />
              </a>
              <button type="button" onClick={closeOffer} className="inline-flex w-full items-center justify-center rounded-full px-6 py-3 text-sm font-medium text-slate-400 transition-colors duration-150 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400" data-id="element-173" data-mp="2jbs9cklj9f0h">
                Nie, dziękuję
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}